import React, { useState, useEffect } from 'react';
import { X, Gift, Calendar, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface ExitIntentPopupProps {
  onClose: () => void;
  variant?: 'newsletter' | 'booking' | 'both';
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({ 
  onClose, 
  variant = 'both' 
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to actual Mailchimp form
      const formData = new FormData();
      formData.append('EMAIL', email);
      formData.append('FNAME', '');
      formData.append('CONSENT', 'true');
      
      await fetch('https://catsabouttowntours.us11.list-manage.com/subscribe/post?u=0d2a33482ae5dafc62ce46cf1&id=5059a17d46&f_id=004f9ce0f0', {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      
      toast({
        title: 'Success!',
        description: 'Check your email for your free NYC cat guide!',
      });
      
      // Track conversion
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'exit_intent_conversion', {
          event_category: 'engagement',
          event_label: 'Exit intent newsletter signup',
          value: 1
        });
      }
      
      onClose();
    } catch (error) {
      toast({
        title: 'Almost there!',
        description: 'Your submission was received. Check your email!',
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookingClick = () => {
    // Track conversion
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exit_intent_booking_click', {
        event_category: 'engagement',
        event_label: 'Exit intent booking click',
        value: 1
      });
    }
    
    window.location.href = '/booking';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close popup"
        >
          <X size={24} />
        </button>
        
        <div className="p-6 text-center">
          <div className="mb-4">
            <Gift className="mx-auto text-primary h-12 w-12" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2 font-montserrat">
            Wait! Don't Miss Out!
          </h2>
          
          {variant === 'newsletter' || variant === 'both' ? (
            <>
              <p className="text-gray-600 mb-4 font-lora">
                Get our <strong>FREE "Secret Cat Spots of NYC" guide</strong> plus first access to new tour dates!
              </p>
              
              <form onSubmit={handleNewsletterSubmit} className="space-y-3 mb-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
                
                <Button 
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Get My Free Guide'}
                </Button>
              </form>
            </>
          ) : null}
          
          {variant === 'booking' || variant === 'both' ? (
            <div className={variant === 'both' ? 'border-t pt-4' : ''}>
              <p className="text-gray-600 mb-4 font-lora">
                Or skip the guide and book your NYC cat history adventure now!
              </p>
              
              <Button 
                onClick={handleBookingClick}
                variant="outline"
                className="w-full"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book a Tour
              </Button>
            </div>
          ) : null}
          
          <p className="text-xs text-gray-500 mt-4">
            No spam, just cat stories and NYC history. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;